import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, take, timeout } from 'rxjs';
import { UserDataService } from './user-data.service';
import { CHAT_EXPERIMENT_CONFIG, ChatExperimentConfig } from './chat-experiment.config';

export interface ChatExperimentQuery {
  cityId: number;
  /** Absent for anonymous visitors. */
  userId?: number;
}

/** Accepts the BFF answer wrapped in `data` or flat; anything that is not a boolean is unknown. */
export function extractValidation(body: unknown): boolean | null {
  const source = (body as { data?: unknown })?.data ?? body;
  const validation = (source as { validation?: unknown })?.validation;
  return typeof validation === 'boolean' ? validation : null;
}

/**
 * Asks the experimentation service whether the user is in the chat experiment.
 * Uses HttpClient directly: MuHttpService clears the session token on a 401,
 * and an experiment failure must never log the user out. Resolves null on any failure.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatExperimentClient {
  constructor(
    private http: HttpClient,
    private userDataService: UserDataService,
    @Inject(CHAT_EXPERIMENT_CONFIG) private config: ChatExperimentConfig
  ) {}

  validate(query: ChatExperimentQuery): Observable<boolean | null> {
    let params = new HttpParams().set('expName', this.config.name).set('cityId', String(query.cityId));
    if (query.userId !== undefined) {
      params = params.set('userId', String(query.userId));
    }
    return this.userDataService.getTokenLocal().pipe(
      take(1),
      switchMap(token => {
        const headers = token ? new HttpHeaders({ 'access-token': token }) : new HttpHeaders();
        return this.http.get(this.config.endpoint, { params, headers });
      }),
      timeout(this.config.timeoutMs),
      map(extractValidation),
      catchError(() => of(null))
    );
  }
}
